import { memo } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { FairUseCoverageIssue } from "./fair-use-coverage-utils"
import type {
  FairUseReconciliationRow,
  FairUseReconciliationThresholds,
} from "@/api/admin/fair-use-reconciliation"

import { FAIR_USE_RECONCILIATION_DAYS } from "@/api/admin/fair-use-reconciliation"
import { AlertTriangle, Gauge, RefreshCw } from "@/components/icons"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

import {
  FAIR_USE_COVERAGE_TABLE_DAYS,
  fairUseCoverageIssues,
  fairUseCoveragePctTone,
  fairUseCoveragePoints,
  fairUseCoveragePolyline,
  fairUseCoverageToneClass,
  fairUseDeltaTone,
  fairUseLatestCoverageDay,
  fairUsePricedTone,
  formatCoverageDay,
  formatCoverageDelta,
  formatCoveragePercent,
} from "./fair-use-coverage-utils"
import { fairUseErrorDescription, isFairUseNotConfiguredError } from "./fair-use-utils"
import {
  useFairUseReconciliationQuery,
  useRunFairUseReconciliationMutation,
} from "../../hooks/queries/fair-use-reconciliation"

const SPARKLINE_WIDTH = 240
const SPARKLINE_HEIGHT = 32

/**
 * "Metering coverage" — how much of what Azure served the ledger can account
 * for, night by night.
 *
 * The card exists to answer one question an invoice cannot: *is what we meter
 * what we are billed?* The nightly job compares the ledger against Azure
 * Monitor per deployment; this renders the last {@link FAIR_USE_RECONCILIATION_DAYS}
 * days of that answer.
 *
 * Two display rules carry the whole point of the feature:
 *
 * - **"priced 0 %" is a problem, not a calm state.** A day where everything was
 *   accounted for but nothing carried a price is a day we served usage for
 *   free without noticing.
 * - **Unknown is a problem too.** Azure Monitor lags up to an hour and the job
 *   runs on the leader only, so an absent number means "we cannot see", never
 *   "there was nothing". Days nobody measured are missing from the window and
 *   said so; they are never drawn as a zero.
 *
 * Unmatched deployments are always listed: they are the likeliest cause of a
 * coverage gap, and hiding them would make the gap unexplainable.
 */
export const FairUseCoverageCard = memo(function FairUseCoverageCard({
  isPlatformAdmin,
}: {
  isPlatformAdmin: boolean
}) {
  const { t, i18n } = useTranslation()
  const locale = i18n.language || "sv"
  const query = useFairUseReconciliationQuery(FAIR_USE_RECONCILIATION_DAYS)
  const runMutation = useRunFairUseReconciliationMutation()

  const unknownLabel = t("adminDashboard.fairUse.coverage.unknown", "Unknown")

  const handleRun = () => {
    runMutation.mutate(
      {},
      {
        onSuccess: () =>
          toast.success(
            t(
              "adminDashboard.fairUse.coverage.runStarted",
              "Measuring yesterday — the result appears when the run has finished."
            )
          ),
        onError: (error: unknown) =>
          toast.error(
            t("adminDashboard.fairUse.coverage.runError", "Could not start the measurement"),
            { description: fairUseErrorDescription(error, t) }
          ),
      }
    )
  }

  const rows = query.data?.reconciliations ?? []
  const thresholds = query.data?.thresholds ?? { token_pct: 1, sek_pct: 2 }
  const latest = fairUseLatestCoverageDay(rows)
  const points = fairUseCoveragePoints(rows)
  const polyline = fairUseCoveragePolyline(points, SPARKLINE_WIDTH, SPARKLINE_HEIGHT)
  const canRun = isPlatformAdmin && query.data?.can_run === true

  return (
    <section
      aria-label={t("adminDashboard.fairUse.coverage.title", "Metering coverage")}
      className="space-y-3 rounded-xl border p-4"
      data-testid="fair-use-coverage-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex min-w-0 items-start gap-2">
          <Gauge className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 space-y-1">
            <h3 className="text-sm font-medium">
              {t("adminDashboard.fairUse.coverage.title", "Metering coverage")}
            </h3>
            <p className="text-muted-foreground text-xs">
              {t(
                "adminDashboard.fairUse.coverage.caption",
                "How much of what Azure served the usage log can account for, night by night."
              )}
            </p>
          </div>
        </div>
        {canRun && (
          <Button
            disabled={runMutation.isPending}
            size="sm"
            type="button"
            variant="outline"
            onClick={handleRun}
          >
            <RefreshCw className={`mr-2 size-4 ${runMutation.isPending ? "animate-spin" : ""}`} />
            {t("adminDashboard.fairUse.coverage.run", "Run for yesterday")}
          </Button>
        )}
      </div>

      <CoverageBody
        error={query.error}
        isLoading={query.isLoading}
        latest={latest}
        locale={locale}
        points={points.length}
        polyline={polyline}
        rows={rows}
        thresholds={thresholds}
        unknownLabel={unknownLabel}
      />
    </section>
  )
})

function CoverageBody({
  error,
  isLoading,
  latest,
  locale,
  points,
  polyline,
  rows,
  thresholds,
  unknownLabel,
}: {
  error: unknown
  isLoading: boolean
  latest: FairUseReconciliationRow | undefined
  locale: string
  points: number
  polyline: string
  rows: FairUseReconciliationRow[]
  thresholds: FairUseReconciliationThresholds
  unknownLabel: string
}) {
  const { t } = useTranslation()

  if (isLoading) {
    return (
      <div className="space-y-2" data-testid="fair-use-coverage-loading">
        <Skeleton className="h-16 w-full rounded-lg" />
        <Skeleton className="h-24 w-full rounded-lg" />
      </div>
    )
  }

  if (error) {
    // "Not provisioned here" is a fact about the deployment, not a failure the
    // admin can act on — it gets the quiet treatment; everything else does not.
    if (isFairUseNotConfiguredError(error)) {
      return (
        <p className="text-muted-foreground text-sm">
          {t(
            "adminDashboard.fairUse.coverage.notAvailable",
            "Metering coverage is not available on this deployment."
          )}
        </p>
      )
    }
    return (
      <CoverageProblem>
        {t("adminDashboard.fairUse.coverage.loadError", "Could not load the metering coverage")}{" "}
        {fairUseErrorDescription(error, t)}
      </CoverageProblem>
    )
  }

  if (!latest) {
    // Nothing measured is NOT "nothing wrong": it is a window we cannot see.
    return (
      <CoverageProblem>
        {t(
          "adminDashboard.fairUse.coverage.empty",
          "No night in the last {{days}} days has been measured yet, so coverage is unknown.",
          { days: FAIR_USE_RECONCILIATION_DAYS }
        )}
      </CoverageProblem>
    )
  }

  return (
    <>
      <CoverageHeadline
        locale={locale}
        row={latest}
        thresholds={thresholds}
        unknownLabel={unknownLabel}
      />
      {polyline && <CoverageSparkline days={points} polyline={polyline} />}
      <CoverageTable
        locale={locale}
        rows={rows.slice(0, FAIR_USE_COVERAGE_TABLE_DAYS)}
        thresholds={thresholds}
        unknownLabel={unknownLabel}
      />
      {rows.length > FAIR_USE_COVERAGE_TABLE_DAYS && (
        <p className="text-muted-foreground text-xs">
          {t(
            "adminDashboard.fairUse.coverage.showingDays",
            "Showing the last {{shown}} of {{measured}} measured nights.",
            { shown: FAIR_USE_COVERAGE_TABLE_DAYS, measured: rows.length }
          )}
        </p>
      )}
    </>
  )
}

/** The amber banner every "we cannot see this" state shares. */
function CoverageProblem({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="border-warning/50 bg-warning/10 text-warning-foreground flex items-start gap-2 rounded-lg border p-3 text-sm"
      data-testid="fair-use-coverage-problem"
      role="status"
    >
      <AlertTriangle className="mt-0.5 size-4 shrink-0" />
      <span>{children}</span>
    </div>
  )
}

/** The newest measured night: four numbers and what is wrong with them. */
function CoverageHeadline({
  locale,
  row,
  thresholds,
  unknownLabel,
}: {
  locale: string
  row: FairUseReconciliationRow
  thresholds: FairUseReconciliationThresholds
  unknownLabel: string
}) {
  const { t } = useTranslation()
  const issues = fairUseCoverageIssues(row, thresholds)

  return (
    <div className="space-y-3" data-testid="fair-use-coverage-latest">
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <CoverageStat
          label={t("adminDashboard.fairUse.coverage.coverage", "Coverage")}
          toneClass={fairUseCoverageToneClass(fairUseCoveragePctTone(row, thresholds))}
          value={formatCoveragePercent(row.coverage_pct, locale, unknownLabel)}
        />
        <CoverageStat
          label={t("adminDashboard.fairUse.coverage.priced", "With a price")}
          toneClass={fairUseCoverageToneClass(fairUsePricedTone(row.priced_pct))}
          value={formatCoveragePercent(row.priced_pct, locale, unknownLabel)}
        />
        <CoverageStat
          label={t("adminDashboard.fairUse.coverage.tokenDelta", "Token difference")}
          value={formatCoverageDelta(row.delta_tokens_pct, locale, unknownLabel)}
          toneClass={fairUseCoverageToneClass(
            fairUseDeltaTone(row.delta_tokens_pct, thresholds.token_pct)
          )}
        />
        <CoverageStat
          label={t("adminDashboard.fairUse.coverage.sekDelta", "SEK difference")}
          value={formatCoverageDelta(row.delta_sek_pct, locale, unknownLabel)}
          toneClass={fairUseCoverageToneClass(
            fairUseDeltaTone(row.delta_sek_pct, thresholds.sek_pct)
          )}
        />
      </div>

      <p className="text-muted-foreground text-xs">
        {t("adminDashboard.fairUse.coverage.measuredDay", "Night of {{day}} (UTC)", {
          day: formatCoverageDay(row.day, locale),
        })}
      </p>

      {issues.length > 0 && (
        <ul className="flex flex-wrap gap-2" data-testid="fair-use-coverage-issues">
          {issues.map((issue) => (
            <li key={issue}>
              <Badge variant="warning">{coverageIssueLabel(issue, row, t)}</Badge>
            </li>
          ))}
        </ul>
      )}

      <CoverageNotes notes={row.notes} />
    </div>
  )
}

/**
 * What the run itself reported.
 *
 * Every way the night degraded is recorded as a note — a configuration key that
 * was never set, one account Azure would not answer for, a storage container
 * that could not be read. Without them every degraded night reads as the same
 * "Azure reported nothing to compare with", and the admin cannot tell a lagging
 * Azure from a setting nobody filled in.
 */
function CoverageNotes({ notes }: { notes: string[] }) {
  const { t } = useTranslation()

  if (notes.length === 0) return null

  return (
    <div className="space-y-1" data-testid="fair-use-coverage-notes">
      <p className="text-muted-foreground text-xs font-medium">
        {t("adminDashboard.fairUse.coverage.notes", "What the run reported")}
      </p>
      <ul className="text-muted-foreground list-disc space-y-0.5 ps-4 text-xs">
        {notes.map((note) => (
          <li key={note}>{note}</li>
        ))}
      </ul>
    </div>
  )
}

function CoverageStat({
  label,
  toneClass,
  value,
}: {
  label: string
  toneClass: string
  value: string
}) {
  return (
    <div className="bg-muted/40 rounded-lg p-3">
      <div className="text-muted-foreground text-xs">{label}</div>
      <div className={`text-lg font-semibold ${toneClass}`}>{value}</div>
    </div>
  )
}

/** Coverage over the window. Gaps in the data are gaps in the line, not zeroes. */
function CoverageSparkline({ days, polyline }: { days: number; polyline: string }) {
  const { t } = useTranslation()
  const label = t(
    "adminDashboard.fairUse.coverage.sparklineLabel",
    "Coverage over the last {{days}} measured nights",
    { days }
  )

  return (
    <svg
      aria-label={label}
      className="text-foreground h-8 w-full"
      data-testid="fair-use-coverage-sparkline"
      preserveAspectRatio="none"
      role="img"
      viewBox={`0 0 ${SPARKLINE_WIDTH} ${SPARKLINE_HEIGHT}`}
    >
      <polyline
        fill="none"
        points={polyline}
        stroke="currentColor"
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        vectorEffect="non-scaling-stroke"
      />
    </svg>
  )
}

function CoverageTable({
  locale,
  rows,
  thresholds,
  unknownLabel,
}: {
  locale: string
  rows: FairUseReconciliationRow[]
  thresholds: FairUseReconciliationThresholds
  unknownLabel: string
}) {
  const { t } = useTranslation()

  return (
    <Table data-testid="fair-use-coverage-table">
      <TableHeader>
        <TableRow>
          <TableHead>{t("adminDashboard.fairUse.coverage.day", "Night")}</TableHead>
          <TableHead>{t("adminDashboard.fairUse.coverage.coverage", "Coverage")}</TableHead>
          <TableHead>{t("adminDashboard.fairUse.coverage.priced", "With a price")}</TableHead>
          <TableHead>
            {t("adminDashboard.fairUse.coverage.tokenDelta", "Token difference")}
          </TableHead>
          <TableHead>{t("adminDashboard.fairUse.coverage.sekDelta", "SEK difference")}</TableHead>
          <TableHead>{t("adminDashboard.fairUse.coverage.findings", "Findings")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {rows.map((row) => {
          const issues = fairUseCoverageIssues(row, thresholds)
          return (
            <TableRow key={row.day} data-testid={`fair-use-coverage-row-${row.day}`}>
              <TableCell className="whitespace-nowrap">
                {formatCoverageDay(row.day, locale)}
              </TableCell>
              <TableCell
                className={fairUseCoverageToneClass(fairUseCoveragePctTone(row, thresholds))}
              >
                {formatCoveragePercent(row.coverage_pct, locale, unknownLabel)}
              </TableCell>
              <TableCell className={fairUseCoverageToneClass(fairUsePricedTone(row.priced_pct))}>
                {formatCoveragePercent(row.priced_pct, locale, unknownLabel)}
              </TableCell>
              <TableCell>
                {formatCoverageDelta(row.delta_tokens_pct, locale, unknownLabel)}
              </TableCell>
              <TableCell>{formatCoverageDelta(row.delta_sek_pct, locale, unknownLabel)}</TableCell>
              <TableCell>
                <div className="flex flex-wrap gap-1">
                  {row.ptu_deployments.length > 0 && (
                    <Badge
                      variant="secondary"
                      title={t(
                        "adminDashboard.fairUse.coverage.ptuTitle",
                        "Reserved capacity: compared on tokens, left out of the SEK difference ({{names}})",
                        { names: row.ptu_deployments.join(", ") }
                      )}
                    >
                      {t("adminDashboard.fairUse.coverage.ptu", "Reserved capacity")}
                    </Badge>
                  )}
                  {issues.map((issue) => (
                    <Badge key={issue} variant="warning">
                      {coverageIssueLabel(issue, row, t)}
                    </Badge>
                  ))}
                  {issues.length === 0 && row.ptu_deployments.length === 0 && (
                    <span className="text-muted-foreground text-xs">
                      {t("adminDashboard.fairUse.coverage.noFindings", "Nothing to report")}
                    </span>
                  )}
                </div>
              </TableCell>
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )
}

/**
 * One issue as a sentence.
 *
 * `unmapped` names the deployments rather than counting them: the name is what
 * an admin takes to the model configuration, and a count is only a nag.
 */
function coverageIssueLabel(
  issue: FairUseCoverageIssue,
  row: FairUseReconciliationRow,
  t: (key: string, defaultValue: string, options?: Record<string, unknown>) => string
): string {
  switch (issue) {
    case "driftTwoDays":
      return t(
        "adminDashboard.fairUse.coverage.issues.driftTwoDays",
        "Difference above the limit two nights running"
      )
    case "drift":
      return t("adminDashboard.fairUse.coverage.issues.drift", "Difference above the limit")
    case "pricedZero":
      return t("adminDashboard.fairUse.coverage.issues.pricedZero", "Nothing carried a price")
    case "coverageUnknown":
      return t("adminDashboard.fairUse.coverage.issues.coverageUnknown", "Coverage unknown")
    case "deltaUnknown":
      return t(
        "adminDashboard.fairUse.coverage.issues.deltaUnknown",
        "Azure reported nothing to compare with"
      )
    case "unmapped":
      return t(
        "adminDashboard.fairUse.coverage.issues.unmapped",
        "Deployments without a matching model: {{names}}",
        { names: row.unmapped.join(", ") }
      )
  }
}
